import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import jsPDF from 'jspdf';
import './SchoolDashboard.css';

function PaymentsReceipts() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // Selectors
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState('name'); // 'name', 'admission_no', 'class'
  
  // Students
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedCollectionClass, setSelectedCollectionClass] = useState('all');
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [collectionSummary, setCollectionSummary] = useState({
    expectedRevenue: 0,
    collectedAmount: 0,
    outstandingBalance: 0,
    studentCount: 0,
    billingCount: 0
  });
  
  // Payment Entry (Credit = Money In)
  const [paymentForm, setPaymentForm] = useState({
    payment_for: '',
    payment_method: '',
    reference_number: '',
    amount_paid: '',
    discount_applied: '',
    notes: ''
  });
  
  // Payment Adjustment (Debit = Money Out)
  const [adjustmentForm, setAdjustmentForm] = useState({
    adjustment_for: '',
    reference_number: '',
    amount: '',
    reason: '',
    notes: ''
  });
  
  // Receipt History
  const [receipts, setReceipts] = useState([]);
  const [reversals, setReversals] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  
  // UI State
  const [activeTab, setActiveTab] = useState('payment'); // 'payment' or 'adjustment'
  const [saving, setSaving] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState(null);
  const [showPaymentSuccessPopup, setShowPaymentSuccessPopup] = useState(false);
  const [showAdjustmentSuccessPopup, setShowAdjustmentSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Helper function to get base class name without section
  const getClassDisplayName = (className) => {
    if (!className) return 'No Class';
    if (className.includes(' - ')) {
      return className.split(' - ')[0].trim();
    }
    return className.trim();
  };

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return parseFloat(num || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getCurrencySymbol = (currency) => {
    const currencySymbols = {
      PKR: '₨',
      USD: '$',
      EUR: '€',
      GBP: '£',
      AED: 'د.إ',
      SAR: '﷼',
      INR: '₹',
      KES: 'KSh',
      NGN: '₦',
      ZAR: 'R'
    };

    return currencySymbols[currency] || currency || '₨';
  };

  const formatCurrency = (amount) => `${getCurrencySymbol(schoolData?.currency)}${formatNumber(amount)}`;

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (!storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchAcademicYears();
      fetchTerms();
      fetchClasses();
      fetchSchoolLogo();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id && selectedAcademicYear) {
      fetchStudents();
    }
  }, [schoolData?.id, selectedBranchId, selectedAcademicYear]);

  useEffect(() => {
    if (selectedStudent) {
      fetchReceipts();
      fetchReversals();
    }
  }, [selectedStudent, selectedAcademicYear, selectedTerm]);

  useEffect(() => {
    filterStudents();
  }, [searchQuery, searchBy, students]);

  useEffect(() => {
    if (schoolData?.id && selectedAcademicYear) {
      fetchCollectionSummary();
    } else {
      setCollectionSummary({
        expectedRevenue: 0,
        collectedAmount: 0,
        outstandingBalance: 0,
        studentCount: 0,
        billingCount: 0
      });
    }
    // Existing page data loaders are plain functions; keep this summary refresh scoped to filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolData?.id, selectedAcademicYear, selectedBranchId, selectedCollectionClass]);

  const fetchAcademicYears = async () => {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: false });

      if (error) {
        console.error('Error fetching academic years:', error);
        return;
      }

      setAcademicYears(data || []);
      if (data && data.length > 0 && !selectedAcademicYear) {
        setSelectedAcademicYear(data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchTerms = async () => {
    try {
      const { data, error } = await supabase
        .from('terms')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching terms:', error);
        return;
      }

      setTerms(data || []);
      if (data && data.length > 0 && !selectedTerm) {
        setSelectedTerm(data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('id, class_name, branch_id')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('class_name', { ascending: true });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching classes:', error);
        setClasses([]);
        return;
      }

      setClasses(data || []);
      if (selectedCollectionClass !== 'all' && !(data || []).some(cls => String(cls.id) === String(selectedCollectionClass))) {
        setSelectedCollectionClass('all');
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setClasses([]);
    }
  };

  const fetchCollectionSummary = async () => {
    try {
      setCollectionLoading(true);

      let studentsQuery = supabase
        .from('students')
        .select('id, class_id')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        studentsQuery = studentsQuery.eq('branch_id', selectedBranchId);
      }

      if (selectedCollectionClass !== 'all') {
        studentsQuery = studentsQuery.eq('class_id', selectedCollectionClass);
      }

      const { data: summaryStudents, error: studentsError } = await studentsQuery;

      if (studentsError) {
        console.error('Error fetching students for collection summary:', studentsError);
        return;
      }

      const studentIds = (summaryStudents || []).map(student => student.id);

      if (studentIds.length === 0) {
        setCollectionSummary({
          expectedRevenue: 0,
          collectedAmount: 0,
          outstandingBalance: 0,
          studentCount: 0,
          billingCount: 0
        });
        return;
      }

      let billingQuery = supabase
        .from('student_billing')
        .select('id, student_id, amount, discount, balance')
        .eq('school_id', schoolData.id)
        .eq('academic_year_id', selectedAcademicYear)
        .in('student_id', studentIds);

      if (selectedBranchId) {
        billingQuery = billingQuery.eq('branch_id', selectedBranchId);
      }

      const { data: billingData, error: billingError } = await billingQuery;

      if (billingError) {
        console.error('Error fetching billing summary:', billingError);
      }

      let paymentsQuery = supabase
        .from('student_payments')
        .select('amount_paid')
        .eq('school_id', schoolData.id)
        .eq('academic_year_id', selectedAcademicYear)
        .in('student_id', studentIds);

      if (selectedBranchId) {
        paymentsQuery = paymentsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: paymentsData, error: paymentsError } = await paymentsQuery;

      if (paymentsError) {
        console.error('Error fetching collection summary payments:', paymentsError);
      }

      let adjustmentsQuery = supabase
        .from('payment_adjustments')
        .select('amount')
        .eq('school_id', schoolData.id)
        .eq('academic_year_id', selectedAcademicYear)
        .in('student_id', studentIds);

      if (selectedBranchId) {
        adjustmentsQuery = adjustmentsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: adjustmentsData, error: adjustmentsError } = await adjustmentsQuery;

      if (adjustmentsError) {
        console.error('Error fetching collection summary adjustments:', adjustmentsError);
      }

      const expectedRevenue = (billingData || []).reduce((sum, item) => {
        const balance = item.balance ?? (parseFloat(item.amount || 0) - parseFloat(item.discount || 0));
        return sum + parseFloat(balance || 0);
      }, 0);
      const totalPayments = (paymentsData || []).reduce((sum, payment) => sum + parseFloat(payment.amount_paid || 0), 0);
      const totalAdjustments = (adjustmentsData || []).reduce((sum, adjustment) => sum + parseFloat(adjustment.amount || 0), 0);
      const collectedAmount = Math.max(totalPayments - totalAdjustments, 0);
      const outstandingBalance = Math.max(expectedRevenue - collectedAmount, 0);

      setCollectionSummary({
        expectedRevenue,
        collectedAmount,
        outstandingBalance,
        studentCount: studentIds.length,
        billingCount: (billingData || []).length
      });
    } catch (error) {
      console.error('Error fetching collection summary:', error);
      setCollectionSummary({
        expectedRevenue: 0,
        collectedAmount: 0,
        outstandingBalance: 0,
        studentCount: 0,
        billingCount: 0
      });
    } finally {
      setCollectionLoading(false);
    }
  };

  const fetchSchoolLogo = async () => {
    if (!schoolData?.logo_url) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('school-logos')
        .download(schoolData.logo_url);
      
      if (error) throw error;
      
      const url = URL.createObjectURL(data);
      setSchoolLogo(url);
    } catch (error) {
      console.error('Error fetching school logo:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      let query = supabase
        .from('students')
        .select(`
          id,
          first_name,
          middle_name,
          last_name,
          student_id_no,
          class_id,
          classes:class_id (id, class_name)
        `)
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('first_name', { ascending: true });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      setStudents(data || []);
      setFilteredStudents(data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const filterStudents = () => {
    if (!searchQuery.trim()) {
      setFilteredStudents(students);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = students.filter(student => {
      if (searchBy === 'name') {
        const fullName = `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.toLowerCase();
        return fullName.includes(query);
      } else if (searchBy === 'admission_no') {
        return (student.student_id_no || '').toLowerCase().includes(query);
      } else if (searchBy === 'class') {
        const className = getClassDisplayName(student.classes?.class_name || '').toLowerCase();
        return className.includes(query);
      }
      return false;
    });

    setFilteredStudents(filtered);
  };

  const fetchReceipts = async () => {
    if (!selectedStudent) return;

    try {
      setLoadingReceipts(true);
      let query = supabase
        .from('student_payments')
        .select('*')
        .eq('student_id', selectedStudent.id)
        .eq('school_id', schoolData.id)
        .order('payment_date', { ascending: false });

      if (selectedAcademicYear) {
        query = query.eq('academic_year_id', selectedAcademicYear);
      }

      if (selectedTerm) {
        const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || selectedTerm;
        query = query.eq('term', termName);
      }

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching receipts:', error);
        setReceipts([]);
        return;
      }

      setReceipts(data || []);
    } catch (error) {
      console.error('Error:', error);
      setReceipts([]);
    } finally {
      setLoadingReceipts(false);
    }
  };

  const fetchReversals = async () => {
    if (!selectedStudent) return;

    try {
      let query = supabase
        .from('payment_adjustments')
        .select('*')
        .eq('student_id', selectedStudent.id)
        .eq('school_id', schoolData.id)
        .eq('type', 'debit')
        .order('adjustment_date', { ascending: false });

      if (selectedAcademicYear) {
        query = query.eq('academic_year_id', selectedAcademicYear);
      }

      if (selectedTerm) {
        const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || selectedTerm;
        query = query.eq('term', termName);
      }

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching reversals:', error);
        setReversals([]);
        return;
      }

      setReversals(data || []);
    } catch (error) {
      console.error('Error:', error);
      setReversals([]);
    }
  };

  const generateReceiptNumber = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `RCP-${timestamp}-${random}`;
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdjustmentInputChange = (e) => {
    const { name, value } = e.target;
    setAdjustmentForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSavePayment = async () => {
    if (!selectedStudent) {
      alert('Please select a student first.');
      return;
    }

    if (!paymentForm.payment_for || !paymentForm.payment_method || !paymentForm.amount_paid) {
      alert('Please fill in all required fields (Payment For, Payment Method, Amount Paid).');
      return;
    }

    setSaving(true);
    try {
      const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || selectedTerm || 'N/A';
      const receiptNumber = generateReceiptNumber();

      const paymentData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        academic_year_id: selectedAcademicYear,
        term: termName,
        student_id: selectedStudent.id,
        receipt_number: receiptNumber,
        payment_for: paymentForm.payment_for,
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || null,
        amount_paid: parseFloat(paymentForm.amount_paid || 0),
        discount_applied: parseFloat(paymentForm.discount_applied || 0),
        notes: paymentForm.notes || null,
        payment_date: new Date().toISOString().split('T')[0],
        type: 'credit'
      };

      const { data: payment, error: paymentError } = await supabase
        .from('student_payments')
        .insert(paymentData)
        .select()
        .single();

      if (paymentError) {
        throw paymentError;
      }

      // Update invoice status if payment covers the full amount
      if (selectedStudent && selectedAcademicYear) {
        // Find related invoices and update their status
        const { data: invoices } = await supabase
          .from('student_invoices')
          .select('*')
          .eq('student_id', selectedStudent.id)
          .eq('academic_year_id', selectedAcademicYear)
          .eq('term', termName);

        if (invoices && invoices.length > 0) {
          for (const invoice of invoices) {
            const totalPaid = parseFloat(payment.amount_paid || 0);
            const totalDue = parseFloat(invoice.total_balance || 0);
            
            let newStatus = 'unpaid';
            if (totalPaid >= totalDue) {
              newStatus = 'paid';
            } else if (totalPaid > 0) {
              newStatus = 'partial';
            }

            await supabase
              .from('student_invoices')
              .update({ status: newStatus })
              .eq('id', invoice.id);
          }
        }
      }

      // Show success popup
      setSuccessData({
        receipt_number: payment.receipt_number,
        amount: payment.amount_paid,
        discount: payment.discount_applied,
        payment_for: payment.payment_for
      });
      setShowPaymentSuccessPopup(true);

      // Reset form
      setPaymentForm({
        payment_for: '',
        payment_method: '',
        reference_number: '',
        amount_paid: '',
        discount_applied: '',
        notes: ''
      });

      await fetchReceipts();
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('Failed to save payment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdjustment = async () => {
    if (!selectedStudent) {
      alert('Please select a student first.');
      return;
    }

    if (!adjustmentForm.adjustment_for || !adjustmentForm.amount || !adjustmentForm.reason) {
      alert('Please fill in all required fields (Adjustment For, Amount, Reason).');
      return;
    }

    setSaving(true);
    try {
      const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || selectedTerm || 'N/A';
      const receiptNumber = generateReceiptNumber();

      const adjustmentData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        academic_year_id: selectedAcademicYear,
        term: termName,
        student_id: selectedStudent.id,
        receipt_number: receiptNumber,
        adjustment_for: adjustmentForm.adjustment_for,
        reference_number: adjustmentForm.reference_number || null,
        amount: parseFloat(adjustmentForm.amount || 0),
        reason: adjustmentForm.reason,
        notes: adjustmentForm.notes || null,
        adjustment_date: new Date().toISOString().split('T')[0],
        type: 'debit'
      };

      const { data: adjustment, error: adjustmentError } = await supabase
        .from('payment_adjustments')
        .insert(adjustmentData)
        .select()
        .single();

      if (adjustmentError) {
        throw adjustmentError;
      }

      // Show success popup
      setSuccessData({
        receipt_number: adjustment.receipt_number,
        amount: adjustment.amount,
        adjustment_for: adjustment.adjustment_for,
        reason: adjustment.reason
      });
      setShowAdjustmentSuccessPopup(true);

      // Reset form
      setAdjustmentForm({
        adjustment_for: '',
        reference_number: '',
        amount: '',
        reason: '',
        notes: ''
      });

      await fetchReversals();
    } catch (error) {
      console.error('Error saving adjustment:', error);
      alert('Failed to save adjustment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const generateReceiptPDF = (receipt, type = 'payment') => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    const student = selectedStudent;
    const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
    const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));

    // School Logo (if available)
    if (schoolLogo) {
      try {
        const img = new Image();
        img.src = schoolLogo;
        doc.addImage(img, 'PNG', margin, yPos, 40, 20);
        yPos += 25;
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
      }
    }

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(type === 'payment' ? 'PAYMENT RECEIPT' : 'PAYMENT ADJUSTMENT', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // School Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(schoolData.school_name || 'School Name', margin, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (schoolData.address) {
      doc.text(schoolData.address, margin, yPos);
      yPos += 5;
    }
    if (schoolData.phone) {
      doc.text(`Phone: ${schoolData.phone}`, margin, yPos);
      yPos += 5;
    }
    if (schoolData.email) {
      doc.text(`Email: ${schoolData.email}`, margin, yPos);
      yPos += 5;
    }
    yPos += 5;

    // Receipt Details
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Receipt Number: ${receipt.receipt_number}`, pageWidth - margin, margin + 5, { align: 'right' });
    doc.text(`Date: ${new Date(receipt.payment_date || receipt.adjustment_date).toLocaleDateString()}`, pageWidth - margin, margin + 10, { align: 'right' });
    
    if (academicYear) {
      doc.text(`Academic Year: ${academicYear.year_name || 'N/A'}`, pageWidth - margin, margin + 15, { align: 'right' });
    }
    if (receipt.term) {
      doc.text(`Term: ${receipt.term}`, pageWidth - margin, margin + 20, { align: 'right' });
    }
    yPos = margin + 30;

    // Student Information
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Student Details:', margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Student Name: ${studentName}`, margin, yPos);
    yPos += 6;
    
    if (student?.student_id_no) {
      doc.text(`Admission No: ${student.student_id_no}`, margin, yPos);
      yPos += 6;
    }
    
    if (student?.classes?.class_name) {
      doc.text(`Class: ${getClassDisplayName(student.classes.class_name)}`, margin, yPos);
      yPos += 6;
    }
    yPos += 5;

    // Line
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Payment/Adjustment Details
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(type === 'payment' ? 'Payment Details:' : 'Adjustment Details:', margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    if (type === 'payment') {
      doc.text(`Payment For: ${receipt.payment_for || 'N/A'}`, margin, yPos);
      yPos += 6;
      doc.text(`Payment Method: ${receipt.payment_method || 'N/A'}`, margin, yPos);
      yPos += 6;
      if (receipt.reference_number) {
        doc.text(`Reference Number: ${receipt.reference_number}`, margin, yPos);
        yPos += 6;
      }
      doc.text(`Amount Paid: Rs ${formatNumber(receipt.amount_paid)}`, margin, yPos);
      yPos += 6;
      if (receipt.discount_applied > 0) {
        doc.text(`Discount Applied: Rs ${formatNumber(receipt.discount_applied)}`, margin, yPos);
        yPos += 6;
      }
    } else {
      doc.text(`Adjustment For: ${receipt.adjustment_for || 'N/A'}`, margin, yPos);
      yPos += 6;
      if (receipt.reference_number) {
        doc.text(`Reference Number: ${receipt.reference_number}`, margin, yPos);
        yPos += 6;
      }
      doc.text(`Amount: Rs ${formatNumber(receipt.amount)}`, margin, yPos);
      yPos += 6;
      doc.text(`Reason: ${receipt.reason || 'N/A'}`, margin, yPos);
      yPos += 6;
    }

    if (receipt.notes) {
      yPos += 5;
      doc.text(`Notes: ${receipt.notes}`, margin, yPos);
      yPos += 6;
    }

    yPos += 5;
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Total
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    const totalAmount = type === 'payment' 
      ? (parseFloat(receipt.amount_paid || 0) - parseFloat(receipt.discount_applied || 0))
      : parseFloat(receipt.amount || 0);
    doc.text(`${type === 'payment' ? 'Total Amount:' : 'Adjustment Amount:'}`, pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text(`Rs ${formatNumber(totalAmount)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 10;

    // Footer
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.text('Thank you!', pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
    doc.text('This is a computer-generated receipt.', pageWidth / 2, yPos, { align: 'center' });

    // Save PDF
    const fileName = `${type === 'payment' ? 'Receipt' : 'Adjustment'}_${receipt.receipt_number}_${studentName.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
  };

  const handlePrintReceipt = (receipt, type = 'payment') => {
    const printWindow = window.open('', '_blank');
    const student = selectedStudent;
    const studentName = `${student?.first_name || ''} ${student?.middle_name || ''} ${student?.last_name || ''}`.trim();
    const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${type === 'payment' ? 'Payment Receipt' : 'Payment Adjustment'} - ${receipt.receipt_number}</title>
          <style>
            @media print {
              @page { margin: 20mm; }
            }
            body {
              font-family: Arial, sans-serif;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            .school-info {
              margin-bottom: 20px;
            }
            .receipt-details {
              text-align: right;
              margin-bottom: 20px;
            }
            .student-info {
              margin-bottom: 20px;
            }
            .details {
              margin-bottom: 20px;
            }
            .total {
              margin-top: 20px;
              text-align: right;
              font-weight: bold;
              font-size: 1.2em;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-style: italic;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${type === 'payment' ? 'PAYMENT RECEIPT' : 'PAYMENT ADJUSTMENT'}</h1>
          </div>
          
          <div class="school-info">
            <h2>${schoolData.school_name || 'School Name'}</h2>
            ${schoolData.address ? `<p>${schoolData.address}</p>` : ''}
            ${schoolData.phone ? `<p>Phone: ${schoolData.phone}</p>` : ''}
            ${schoolData.email ? `<p>Email: ${schoolData.email}</p>` : ''}
          </div>
          
          <div class="receipt-details">
            <p><strong>Receipt Number:</strong> ${receipt.receipt_number}</p>
            <p><strong>Date:</strong> ${new Date(receipt.payment_date || receipt.adjustment_date).toLocaleDateString()}</p>
            ${academicYear ? `<p><strong>Academic Year:</strong> ${academicYear.year_name || 'N/A'}</p>` : ''}
            ${receipt.term ? `<p><strong>Term:</strong> ${receipt.term}</p>` : ''}
          </div>
          
          <div class="student-info">
            <h3>Student Details:</h3>
            <p><strong>Student Name:</strong> ${studentName}</p>
            ${student?.student_id_no ? `<p><strong>Admission No:</strong> ${student.student_id_no}</p>` : ''}
            ${student?.classes?.class_name ? `<p><strong>Class:</strong> ${getClassDisplayName(student.classes.class_name)}</p>` : ''}
          </div>
          
          <div class="details">
            <h3>${type === 'payment' ? 'Payment' : 'Adjustment'} Details:</h3>
            ${type === 'payment' ? `
              <p><strong>Payment For:</strong> ${receipt.payment_for || 'N/A'}</p>
              <p><strong>Payment Method:</strong> ${receipt.payment_method || 'N/A'}</p>
              ${receipt.reference_number ? `<p><strong>Reference Number:</strong> ${receipt.reference_number}</p>` : ''}
              <p><strong>Amount Paid:</strong> Rs ${formatNumber(receipt.amount_paid)}</p>
              ${receipt.discount_applied > 0 ? `<p><strong>Discount Applied:</strong> Rs ${formatNumber(receipt.discount_applied)}</p>` : ''}
            ` : `
              <p><strong>Adjustment For:</strong> ${receipt.adjustment_for || 'N/A'}</p>
              ${receipt.reference_number ? `<p><strong>Reference Number:</strong> ${receipt.reference_number}</p>` : ''}
              <p><strong>Amount:</strong> Rs ${formatNumber(receipt.amount)}</p>
              <p><strong>Reason:</strong> ${receipt.reason || 'N/A'}</p>
            `}
            ${receipt.notes ? `<p><strong>Notes:</strong> ${receipt.notes}</p>` : ''}
          </div>
          
          <div class="total">
            <p><strong>${type === 'payment' ? 'Total Amount:' : 'Adjustment Amount:'}</strong> Rs ${formatNumber(type === 'payment' ? (parseFloat(receipt.amount_paid || 0) - parseFloat(receipt.discount_applied || 0)) : parseFloat(receipt.amount || 0))}</p>
          </div>
          
          <div class="footer">
            <p>Thank you!</p>
            <p>This is a computer-generated receipt.</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handlePublishToParentApp = async (receipt, type = 'payment') => {
    try {
      // Update receipt status to 'published'
      const tableName = type === 'payment' ? 'student_payments' : 'payment_adjustments';
      const { error } = await supabase
        .from(tableName)
        .update({ 
          published_to_parent: true,
          published_at: new Date().toISOString()
        })
        .eq('id', receipt.id);

      if (error) throw error;

      alert(`${type === 'payment' ? 'Receipt' : 'Adjustment'} published to Parent APP successfully!`);
      if (type === 'payment') {
        await fetchReceipts();
      } else {
        await fetchReversals();
      }
    } catch (error) {
      console.error('Error publishing to parent app:', error);
      alert('Failed to publish. Please try again.');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const collectionRate = collectionSummary.expectedRevenue > 0
    ? Math.min((collectionSummary.collectedAmount / collectionSummary.expectedRevenue) * 100, 100)
    : 0;
  const outstandingRate = collectionSummary.expectedRevenue > 0
    ? Math.min((collectionSummary.outstandingBalance / collectionSummary.expectedRevenue) * 100, 100)
    : 0;

  return (
    <div className="school-dashboard">
      {/* Payment Success Popup */}
      {showPaymentSuccessPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '20px',
            padding: '2.5rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            animation: 'slideInScale 0.4s ease',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '150px',
              height: '150px',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Success Icon */}
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'scaleIn 0.5s ease',
                boxShadow: '0 10px 30px rgba(16, 185, 129, 0.4)'
              }}>
                <svg
                  width="50"
                  height="50"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ animation: 'checkmarkDraw 0.6s ease 0.3s both' }}
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>

              {/* Success Message */}
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#1F2937',
                marginBottom: '1rem',
                animation: 'fadeInUp 0.5s ease 0.2s both'
              }}>
                Payment Saved Successfully!
              </h2>

              <p style={{
                fontSize: '1rem',
                color: '#6B7280',
                marginBottom: '1.5rem',
                animation: 'fadeInUp 0.5s ease 0.3s both'
              }}>
                Your payment has been recorded and receipt generated.
              </p>

              {/* Receipt Details */}
              {successData && (
                <div style={{
                  background: '#F9FAFB',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  textAlign: 'left',
                  animation: 'fadeInUp 0.5s ease 0.4s both'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{ color: '#6B7280', fontWeight: '500' }}>Receipt Number:</span>
                    <span style={{ color: '#1F2937', fontWeight: '600' }}>{successData.receipt_number}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{ color: '#6B7280', fontWeight: '500' }}>Payment For:</span>
                    <span style={{ color: '#1F2937', fontWeight: '600' }}>{successData.payment_for}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{ color: '#6B7280', fontWeight: '500' }}>Amount Paid:</span>
                    <span style={{ color: '#10B981', fontWeight: '700', fontSize: '1.1rem' }}>
                      Rs {formatNumber(successData.amount)}
                    </span>
                  </div>
                  {successData.discount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6B7280', fontWeight: '500' }}>Discount:</span>
                      <span style={{ color: '#EF4444', fontWeight: '600' }}>
                        - Rs {formatNumber(successData.discount)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => {
                  setShowPaymentSuccessPopup(false);
                  setSuccessData(null);
                }}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  animation: 'fadeInUp 0.5s ease 0.5s both'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjustment Success Popup */}
      {showAdjustmentSuccessPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '20px',
            padding: '2.5rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            animation: 'slideInScale 0.4s ease',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '150px',
              height: '150px',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              borderRadius: '50%',
              zIndex: 0
            }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Success Icon */}
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'scaleIn 0.5s ease',
                boxShadow: '0 10px 30px rgba(239, 68, 68, 0.4)'
              }}>
                <svg
                  width="50"
                  height="50"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ animation: 'checkmarkDraw 0.6s ease 0.3s both' }}
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>

              {/* Success Message */}
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#1F2937',
                marginBottom: '1rem',
                animation: 'fadeInUp 0.5s ease 0.2s both'
              }}>
                Adjustment Saved Successfully!
              </h2>

              <p style={{
                fontSize: '1rem',
                color: '#6B7280',
                marginBottom: '1.5rem',
                animation: 'fadeInUp 0.5s ease 0.3s both'
              }}>
                Your payment adjustment has been recorded and receipt generated.
              </p>

              {/* Adjustment Details */}
              {successData && (
                <div style={{
                  background: '#F9FAFB',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  textAlign: 'left',
                  animation: 'fadeInUp 0.5s ease 0.4s both'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{ color: '#6B7280', fontWeight: '500' }}>Receipt Number:</span>
                    <span style={{ color: '#1F2937', fontWeight: '600' }}>{successData.receipt_number}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{ color: '#6B7280', fontWeight: '500' }}>Adjustment For:</span>
                    <span style={{ color: '#1F2937', fontWeight: '600' }}>{successData.adjustment_for}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{ color: '#6B7280', fontWeight: '500' }}>Reason:</span>
                    <span style={{ color: '#1F2937', fontWeight: '600' }}>{successData.reason}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280', fontWeight: '500' }}>Amount:</span>
                    <span style={{ color: '#EF4444', fontWeight: '700', fontSize: '1.1rem' }}>
                      Rs {formatNumber(successData.amount)}
                    </span>
                  </div>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => {
                  setShowAdjustmentSuccessPopup(false);
                  setSuccessData(null);
                }}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  animation: 'fadeInUp 0.5s ease 0.5s both'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInScale {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes checkmarkDraw {
          from {
            stroke-dasharray: 0, 24;
            opacity: 0;
          }
          to {
            stroke-dasharray: 24, 24;
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content" style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: '1.5rem'
      }}>
        <div className="content-header" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginBottom: '1rem'
        }}>
          <h1>Payments & Receipts</h1>
          <p>Capture payment details and manage receipts</p>
        </div>

        {/* Selectors Row */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '2rem',
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div className="form-group" style={{ minWidth: '200px', flex: '1' }}>
            <label htmlFor="academic_year">Academic Year *</label>
            <select
              id="academic_year"
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">Select Academic Year</option>
              {academicYears.map(year => (
                <option key={year.id} value={year.id}>
                  {year.year_name || `${new Date(year.start_date).getFullYear()}-${new Date(year.end_date).getFullYear()}`}
                </option>
              ))}
            </select>
          </div>

          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />

          <div className="form-group" style={{ minWidth: '220px', flex: '1' }}>
            <label htmlFor="collection_class">Class-wise Filter</label>
            <select
              id="collection_class"
              value={selectedCollectionClass}
              onChange={(e) => setSelectedCollectionClass(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="all">All Classes</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  {getClassDisplayName(cls.class_name)}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ minWidth: '150px', flex: '1' }}>
            <label htmlFor="term">Term *</label>
            <select
              id="term"
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">Select Term</option>
              {terms.map(term => (
                <option key={term.id} value={term.id}>{term.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Fees Collection Metrics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '14px',
            padding: '1.25rem',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)'
          }}>
            <p style={{ margin: '0 0 0.5rem', color: '#6B7280', fontWeight: 700, fontSize: '0.85rem' }}>Expected Revenue</p>
            <h2 style={{ margin: 0, color: '#1F2937', fontSize: '1.75rem' }}>
              {collectionLoading ? 'Loading...' : formatCurrency(collectionSummary.expectedRevenue)}
            </h2>
            <p style={{ margin: '0.5rem 0 0', color: '#6B7280', fontSize: '0.85rem' }}>
              {collectionSummary.billingCount} billing item{collectionSummary.billingCount === 1 ? '' : 's'}
            </p>
          </div>

          <div style={{
            background: '#FFFFFF',
            border: '1px solid #D1FAE5',
            borderRadius: '14px',
            padding: '1.25rem',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)'
          }}>
            <p style={{ margin: '0 0 0.5rem', color: '#047857', fontWeight: 700, fontSize: '0.85rem' }}>Collected Amount</p>
            <h2 style={{ margin: 0, color: '#059669', fontSize: '1.75rem' }}>
              {collectionLoading ? 'Loading...' : formatCurrency(collectionSummary.collectedAmount)}
            </h2>
            <p style={{ margin: '0.5rem 0 0', color: '#6B7280', fontSize: '0.85rem' }}>
              {collectionRate.toFixed(1)}% collected
            </p>
          </div>

          <div style={{
            background: '#FFFFFF',
            border: '1px solid #FEE2E2',
            borderRadius: '14px',
            padding: '1.25rem',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)'
          }}>
            <p style={{ margin: '0 0 0.5rem', color: '#B91C1C', fontWeight: 700, fontSize: '0.85rem' }}>Outstanding Balance</p>
            <h2 style={{ margin: 0, color: '#DC2626', fontSize: '1.75rem' }}>
              {collectionLoading ? 'Loading...' : formatCurrency(collectionSummary.outstandingBalance)}
            </h2>
            <p style={{ margin: '0.5rem 0 0', color: '#6B7280', fontSize: '0.85rem' }}>
              {outstandingRate.toFixed(1)}% outstanding
            </p>
          </div>
        </div>

        {/* Graphical Representation */}
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '14px',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <div>
              <h3 style={{ margin: '0 0 0.35rem', color: '#1F2937' }}>Fees Collection Performance</h3>
              <p style={{ margin: 0, color: '#6B7280', fontSize: '0.9rem' }}>
                {collectionSummary.studentCount} student{collectionSummary.studentCount === 1 ? '' : 's'} included in the selected filters
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', color: '#4B5563', fontSize: '0.9rem', fontWeight: 600 }}>
              <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#10B981', borderRadius: 3, marginRight: 6 }}></span>Collected</span>
              <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#EF4444', borderRadius: 3, marginRight: 6 }}></span>Outstanding</span>
            </div>
          </div>

          <div style={{ background: '#F3F4F6', borderRadius: '999px', height: '28px', overflow: 'hidden', display: 'flex' }}>
            <div
              style={{
                width: `${collectionRate}%`,
                background: '#10B981',
                transition: 'width 0.3s ease'
              }}
              title={`Collected ${collectionRate.toFixed(1)}%`}
            ></div>
            <div
              style={{
                width: `${outstandingRate}%`,
                background: '#EF4444',
                transition: 'width 0.3s ease'
              }}
              title={`Outstanding ${outstandingRate.toFixed(1)}%`}
            ></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
            <div style={{ background: '#ECFDF5', padding: '1rem', borderRadius: '10px', color: '#065F46', fontWeight: 700 }}>
              Collected: {collectionRate.toFixed(1)}%
            </div>
            <div style={{ background: '#FEF2F2', padding: '1rem', borderRadius: '10px', color: '#991B1B', fontWeight: 700 }}>
              Outstanding: {outstandingRate.toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Search Section */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '2rem',
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div className="form-group" style={{ minWidth: '150px' }}>
            <label>Search By</label>
            <select
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="name">Student Name</option>
              <option value="admission_no">Admission No</option>
              <option value="class">Class</option>
            </select>
          </div>

          <div className="form-group" style={{ minWidth: '250px', flex: '1' }}>
            <label>Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search by ${searchBy === 'name' ? 'Student Name' : searchBy === 'admission_no' ? 'Admission No' : 'Class'}`}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            />
          </div>
        </div>

        {/* Student Selection */}
        {filteredStudents.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Select Student *</label>
            <select
              value={selectedStudent?.id || ''}
              onChange={(e) => {
                const student = filteredStudents.find(s => s.id === parseInt(e.target.value));
                setSelectedStudent(student || null);
              }}
              style={{ width: '100%', maxWidth: '500px', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">Select Student</option>
              {filteredStudents.map(student => (
                <option key={student.id} value={student.id}>
                  {`${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim()} 
                  {student.student_id_no ? ` (${student.student_id_no})` : ''} 
                  {student.classes?.class_name ? ` - ${getClassDisplayName(student.classes.class_name)}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          marginBottom: '1.5rem',
          borderBottom: '2px solid #E5E7EB'
        }}>
          <button
            onClick={() => setActiveTab('payment')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'payment' ? '#FF8C42' : 'transparent',
              color: activeTab === 'payment' ? 'white' : '#374151',
              border: 'none',
              borderBottom: activeTab === 'payment' ? '3px solid #FF8C42' : '3px solid transparent',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
          >
            Payment Entry (Credit = Money In)
          </button>
          <button
            onClick={() => setActiveTab('adjustment')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'adjustment' ? '#FF8C42' : 'transparent',
              color: activeTab === 'adjustment' ? 'white' : '#374151',
              border: 'none',
              borderBottom: activeTab === 'adjustment' ? '3px solid #FF8C42' : '3px solid transparent',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
          >
            Payment Adjustment (Debit = Money Out)
          </button>
        </div>

        {/* Payment Entry Tab */}
        {activeTab === 'payment' && (
          <div className="dashboard-section" style={{ 
            width: '100%', 
            maxWidth: '100%',
            boxSizing: 'border-box',
            padding: '2rem'
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>Payment Entry</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
              <div className="form-group">
                <label htmlFor="payment_for">Payment For *</label>
                <input
                  type="text"
                  id="payment_for"
                  name="payment_for"
                  value={paymentForm.payment_for}
                  onChange={handlePaymentInputChange}
                  placeholder="e.g., Tuition Fee, Term 1 Fees"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="payment_method">Payment Method *</label>
                <select
                  id="payment_method"
                  name="payment_method"
                  value={paymentForm.payment_method}
                  onChange={handlePaymentInputChange}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                >
                  <option value="">Select Payment Method</option>
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Mobile Money">Mobile Money</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="reference_number">Reference Number</label>
                <input
                  type="text"
                  id="reference_number"
                  name="reference_number"
                  value={paymentForm.reference_number}
                  onChange={handlePaymentInputChange}
                  placeholder="Transaction reference"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="amount_paid">Amount Paid *</label>
                <input
                  type="number"
                  id="amount_paid"
                  name="amount_paid"
                  value={paymentForm.amount_paid}
                  onChange={handlePaymentInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="discount_applied">Discount Applied</label>
                <input
                  type="number"
                  id="discount_applied"
                  name="discount_applied"
                  value={paymentForm.discount_applied}
                  onChange={handlePaymentInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes/Remarks</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={paymentForm.notes}
                  onChange={handlePaymentInputChange}
                  placeholder="Additional notes..."
                  rows="3"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB', resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <button
                onClick={handleSavePayment}
                disabled={saving || !selectedStudent}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: saving || !selectedStudent ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  opacity: saving || !selectedStudent ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!saving && selectedStudent) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saving && selectedStudent) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Payment'}
              </button>
            </div>

            {/* Receipt History */}
            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Receipt History</h3>
              {loadingReceipts ? (
                <p>Loading receipts...</p>
              ) : receipts.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                  No receipts found for this student.
                </p>
              ) : (
                <div className="table-container" style={{
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e9ecef'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.9rem'
                  }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB' }}>Receipt Number</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB' }}>Date</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB' }}>Payment For</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>Amount</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #E5E7EB' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipts.map((receipt) => (
                        <tr key={receipt.id}>
                          <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>{receipt.receipt_number}</td>
                          <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>
                            {new Date(receipt.payment_date).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>{receipt.payment_for}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>
                            Rs {formatNumber(receipt.amount_paid - (receipt.discount_applied || 0))}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #E5E7EB' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => generateReceiptPDF(receipt, 'payment')}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#10B981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                📥 Download PDF
                              </button>
                              <button
                                onClick={() => handlePrintReceipt(receipt, 'payment')}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#3B82F6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                🖨️ Print
                              </button>
                              <button
                                onClick={() => handlePublishToParentApp(receipt, 'payment')}
                                disabled={receipt.published_to_parent}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: receipt.published_to_parent ? '#D1D5DB' : '#FF8C42',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  cursor: receipt.published_to_parent ? 'not-allowed' : 'pointer',
                                  opacity: receipt.published_to_parent ? 0.6 : 1
                                }}
                              >
                                {receipt.published_to_parent ? '✅ Published' : '📱 Publish to Parent APP'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Adjustment Tab */}
        {activeTab === 'adjustment' && (
          <div className="dashboard-section" style={{ 
            width: '100%', 
            maxWidth: '100%',
            boxSizing: 'border-box',
            padding: '2rem'
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>Payment Adjustment Entry</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
              <div className="form-group">
                <label htmlFor="adjustment_for">Adjustment For *</label>
                <input
                  type="text"
                  id="adjustment_for"
                  name="adjustment_for"
                  value={adjustmentForm.adjustment_for}
                  onChange={handleAdjustmentInputChange}
                  placeholder="e.g., Refund for Term 1"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="adjustment_reference">Reference Number (Optional)</label>
                <input
                  type="text"
                  id="adjustment_reference"
                  name="reference_number"
                  value={adjustmentForm.reference_number}
                  onChange={handleAdjustmentInputChange}
                  placeholder="Reference number"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="adjustment_amount">Amount *</label>
                <input
                  type="number"
                  id="adjustment_amount"
                  name="amount"
                  value={adjustmentForm.amount}
                  onChange={handleAdjustmentInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="reason">Reason for Adjustment *</label>
                <select
                  id="reason"
                  name="reason"
                  value={adjustmentForm.reason}
                  onChange={handleAdjustmentInputChange}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
                >
                  <option value="">Select Reason</option>
                  <option value="Refund">Refund</option>
                  <option value="Posting Error">Posting Error</option>
                  <option value="Over payment">Over payment</option>
                </select>
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="adjustment_notes">Notes/Remarks</label>
                <textarea
                  id="adjustment_notes"
                  name="notes"
                  value={adjustmentForm.notes}
                  onChange={handleAdjustmentInputChange}
                  placeholder="Additional notes..."
                  rows="3"
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB', resize: 'vertical' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <button
                onClick={handleSaveAdjustment}
                disabled={saving || !selectedStudent}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: saving || !selectedStudent ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  opacity: saving || !selectedStudent ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!saving && selectedStudent) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saving && selectedStudent) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>

            {/* Reversal History */}
            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Reversal History</h3>
              {reversals.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                  No reversals found for this student.
                </p>
              ) : (
                <div className="table-container" style={{
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e9ecef'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.9rem'
                  }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB' }}>Receipt Number</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB' }}>Date</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB' }}>Adjustment For</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', border: '1px solid #E5E7EB' }}>Reason</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>Amount</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #E5E7EB' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reversals.map((reversal) => (
                        <tr key={reversal.id}>
                          <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>{reversal.receipt_number}</td>
                          <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>
                            {new Date(reversal.adjustment_date).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>{reversal.adjustment_for}</td>
                          <td style={{ padding: '0.75rem', border: '1px solid #E5E7EB' }}>{reversal.reason}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', border: '1px solid #E5E7EB' }}>
                            Rs {formatNumber(reversal.amount)}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center', border: '1px solid #E5E7EB' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => handlePrintReceipt(reversal, 'adjustment')}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#3B82F6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                🖨️ Print Receipt
                              </button>
                              <button
                                onClick={() => generateReceiptPDF(reversal, 'adjustment')}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#10B981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  cursor: 'pointer'
                                }}
                              >
                                📥 Download PDF
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentsReceipts;
